import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiService } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Search, FileText, Filter, Sparkles, Download } from 'lucide-react';
import BlueprintCardSkeleton from '@/components/skeletons/BlueprintCardSkeleton';
import type { Blueprint } from '@/types';

export default function BlueprintsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState<'keyword' | 'semantic'>('keyword');
  const [showFilters, setShowFilters] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'date' | 'priority' | 'title'>('date');

  const { data: blueprints, isLoading } = useQuery({
    queryKey: ['blueprints'],
    queryFn: () => apiService.getBlueprints(),
  });

  const { data: searchResults, refetch: performSearch, isFetching: isSearching } = useQuery({
    queryKey: ['search', searchQuery, searchMode],
    queryFn: () => {
      if (searchMode === 'semantic') {
        return apiService.semanticSearch(searchQuery, 'auto');
      } else {
        return apiService.searchBlueprints(searchQuery);
      }
    },
    enabled: false,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      performSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Get display items and apply filters
  let displayItems: Blueprint[] = searchResults?.map((r) => r.blueprint) || blueprints || [];

  // Apply priority filter
  if (priorityFilter) {
    displayItems = displayItems.filter(b => b.priority?.toLowerCase() === priorityFilter.toLowerCase());
  }

  // Apply sorting
  displayItems = [...displayItems].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.metadata.createdAt).getTime() - new Date(a.metadata.createdAt).getTime();
    } else if (sortBy === 'priority') {
      const priorities = { critical: 4, high: 3, medium: 2, low: 1 };
      const aPriority = priorities[a.priority?.toLowerCase() as keyof typeof priorities] || 0;
      const bPriority = priorities[b.priority?.toLowerCase() as keyof typeof priorities] || 0;
      return bPriority - aPriority;
    } else {
      return a.title.localeCompare(b.title);
    }
  });


  return (
    <div className="container h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Blueprints</h1>
          <Link to="/chat">
            <Button>Create New</Button>
          </Link>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchMode === 'semantic' ? "Search by meaning (e.g., 'authentication system')..." : "Search blueprints..."}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
            <Button type="submit" disabled={isSearching}>
              <Search className="mr-2 h-4 w-4" />
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
            {searchQuery && (
              <Button type="button" variant="outline" onClick={handleClearSearch}>
                Clear
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-4 w-4" />
            </Button>
          </div>

          {/* Search Mode Toggle */}
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted-foreground">Search mode:</span>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="searchMode"
                value="keyword"
                checked={searchMode === 'keyword'}
                onChange={(e) => setSearchMode(e.target.value as 'keyword')}
                className="h-4 w-4"
              />
              <span>Keyword</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="searchMode"
                value="semantic"
                checked={searchMode === 'semantic'}
                onChange={(e) => setSearchMode(e.target.value as 'semantic')}
                className="h-4 w-4"
              />
              <span className="flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                Semantic (AI-powered)
              </span>
            </label>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="rounded-lg border bg-card p-4 space-y-3">
              <h3 className="font-semibold">Filters</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Priority</label>
                  <select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">All</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Sort by</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'date' | 'priority' | 'title')}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="date">Date (Newest)</option>
                    <option value="priority">Priority</option>
                    <option value="title">Title</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </form>

        {isLoading ? (
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <BlueprintCardSkeleton key={i} />
            ))}
          </div>
        ) : displayItems.length === 0 ? (
          <div className="text-center text-muted-foreground">
            <FileText className="mx-auto mb-4 h-12 w-12" />
            <p>No blueprints found</p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">
              {displayItems.length} blueprint{displayItems.length !== 1 ? 's' : ''} found
            </div>
            <div className="grid gap-4">
              {displayItems.map((blueprint) => (
                <div
                  key={blueprint.id}
                  className="group relative rounded-lg border bg-card p-6 transition-colors hover:bg-accent"
                >
                  <Link to={`/blueprints/${blueprint.id}`} className="block">
                    <h3 className="mb-2 text-xl font-semibold">{blueprint.title}</h3>
                    <p className="mb-4 text-muted-foreground">{blueprint.description}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {blueprint.priority && (
                        <span className="rounded bg-primary/10 px-2 py-1 text-primary capitalize">
                          {blueprint.priority}
                        </span>
                      )}
                      {blueprint.estimatedComplexity && (
                        <span className="capitalize">{blueprint.estimatedComplexity} complexity</span>
                      )}
                      <span>{new Date(blueprint.metadata.createdAt).toLocaleDateString()}</span>
                    </div>
                  </Link>

                  {/* View Details button - only visible on hover */}
                  <div className="absolute right-4 top-4 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button
                      size="sm"
                      variant="ghost"
                      asChild
                      title="View Details"
                    >
                      <Link to={`/blueprints/${blueprint.id}`}>
                        <Download className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
